import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { doc, collection, addDoc, updateDoc, deleteDoc, onSnapshot, query, orderBy, getDocs, where, getDoc, arrayUnion, arrayRemove, setDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { FaTimes, FaThumbsUp, FaThumbsDown, FaTrash, FaEdit, FaRegComment, FaUserCircle, FaTags, FaHeart } from 'react-icons/fa';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import ConfirmationModal from '../ConfirmationModal';
import { FiLoader } from 'react-icons/fi';

function ShowCompletePostModal({ isOpen, onClose, post: initialPost, sortOrder, onPostUpdated }) {
  const { currentUser } = useAuth();
  const [post, setPost] = useState(initialPost || null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [labelsData, setLabelsData] = useState({});
  const [showDeleteCommentModal, setShowDeleteCommentModal] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  useEffect(() => {
    // Update the local post state when initialPost changes
    setPost(initialPost || null);
  }, [initialPost]);

  // This effect keeps the parent component's post array updated when comments change
  useEffect(() => {
    // When comments change, notify the parent component
    if (isOpen && post?.id && comments.length >= 0 && typeof onPostUpdated === 'function') {
      // Add a slight delay to ensure all comment updates are processed
      const timer = setTimeout(() => {
        onPostUpdated();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [comments.length, isOpen, post?.id, onPostUpdated]);

  useEffect(() => {
    if (!isOpen || !post || !post.id) return;

    // Check admin status
    const checkAdminStatus = async () => {
      try {
        const userDoc = await doc(db, 'users', currentUser.uid);
        const userSnapshot = await getDoc(userDoc);
        if (userSnapshot.exists()) {
          setIsAdmin(userSnapshot.data().isAdmin || false);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };

    // Check if user is following this post
    const checkFollowStatus = async () => {
      try {
        const followingDoc = await getDoc(doc(db, 'followingPosts', currentUser.uid));
        if (followingDoc.exists()) {
          const followingData = followingDoc.data();
          const followingPosts = followingData.posts || [];
          setIsFollowing(followingPosts.includes(post.id));
        } else {
          setIsFollowing(false);
        }
      } catch (error) {
        console.error('Error checking follow status:', error);
        setIsFollowing(false);
      }
    };

    checkAdminStatus();
    checkFollowStatus();

    // Set up live post listener for voting updates
    const postRef = doc(db, 'posts', post.id);
    const unsubscribePost = onSnapshot(postRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const postData = docSnapshot.data();
        setPost(currentPost => ({
          ...currentPost,
          ...postData,
          id: post.id // Ensure we always have the ID
        }));
      } else {
        // Handle case where post might have been deleted
        console.warn('Post no longer exists in database');
      }
    }, (error) => {
      console.error('Error getting post updates:', error);
    });

    // Set up comments listener
    const commentsQuery = query(
      collection(db, 'comments'),
      where('postId', '==', post.id)
    );

    const unsubscribeComments = onSnapshot(commentsQuery, (snapshot) => {
      let allComments = [];
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.comments && Array.isArray(data.comments)) {
          // Map the comments array to include the document ID for potential operations
          const commentsWithIds = data.comments.map((comment, index) => ({
            ...comment,
            id: doc.id,
            commentIndex: index,
            docId: doc.id
          }));
          
          allComments = [...allComments, ...commentsWithIds];
        }
      });
      
      // Sort comments by date if needed
      if (sortOrder === 'recent') {
        allComments.sort((a, b) => {
          if (!a.createdAt || !b.createdAt) return 0;
          return new Date(b.createdAt.toDate()) - new Date(a.createdAt.toDate());
        });
      } else if (sortOrder === 'popular') {
        // Sort by votes (upvotes - downvotes)
        allComments.sort((a, b) => {
          const aVotes = (a.upvoteCount || 0) - (a.downvoteCount || 0);
          const bVotes = (b.upvoteCount || 0) - (b.downvoteCount || 0);
          return bVotes - aVotes || // First sort by votes
            // If votes are equal, sort by newest (as tiebreaker)
            new Date(b.createdAt?.toDate()) - new Date(a.createdAt?.toDate());
        });
      } else {
        // Default: Sort by oldest first
        allComments.sort((a, b) => {
          if (!a.createdAt || !b.createdAt) return 0;
          return new Date(a.createdAt.toDate()) - new Date(b.createdAt.toDate());
        });
      }
      
      setComments(allComments);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
      setLoading(false);
    });

    // Fetch labels data to display label names
    const fetchLabels = async () => {
      try {
        const labelsQuery = query(collection(db, 'forumLabels'));
        const labelsSnapshot = await getDocs(labelsQuery);
        
        const labelsMapping = {};
        labelsSnapshot.docs.forEach(doc => {
          const labelData = doc.data();
          if (labelData.labelId) {
            labelsMapping[labelData.labelId] = labelData.labelName;
          }
        });
        
        setLabelsData(labelsMapping);
      } catch (error) {
        console.error('Error fetching labels:', error);
      }
    };

    fetchLabels();

    return () => {
      unsubscribeComments();
      unsubscribePost();
    };
  }, [isOpen, post?.id, sortOrder, currentUser]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !post || !post.id || isSubmittingComment) return;

    setIsSubmittingComment(true);
    try {
      // First check if a comments document for this post already exists
      const commentsQuery = query(
        collection(db, 'comments'),
        where('postId', '==', post.id)
      );
      
      const snapshot = await getDocs(commentsQuery);
      const existingCommentDocs = snapshot.docs;
      
      const commentData = {
        content: newComment.trim(),
        userId: currentUser.uid,
        username: currentUser.displayName || currentUser.email || 'Anonymous',
        createdAt: new Date(),
        upvoteCount: 0,
        downvoteCount: 0,
        isAdmin: isAdmin
      };

      if (existingCommentDocs.length > 0) {
        // Update existing comments document
        const commentDoc = existingCommentDocs[0];
        const existingComments = commentDoc.data().comments || [];
        
        await updateDoc(doc(db, 'comments', commentDoc.id), {
          comments: [...existingComments, commentData]
        });
      } else {
        // Create new comments document
        await addDoc(collection(db, 'comments'), {
          postId: post.id,
          comments: [commentData],
          createdAt: new Date()
        });
      }

      // Update the commentCount field in the post document
      const postRef = doc(db, 'posts', post.id);
      await updateDoc(postRef, {
        commentCount: (post.commentCount || 0) + 1
      });

      setNewComment('');
      toast.success('Comment added successfully');

      // Notify the parent component that a comment has been added
      if (typeof onPostUpdated === 'function') {
        onPostUpdated();
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const confirmDeleteComment = (comment) => {
    setCommentToDelete(comment);
    setShowDeleteCommentModal(true);
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete || !post || !post.id) return;
    
    console.log('Attempting to delete comment:', {
      docId: commentToDelete.docId,
      commentIndex: commentToDelete.commentIndex,
      userId: currentUser.uid,
      commentUserId: commentToDelete.userId
    });
    
    try {
      // Get the document that contains the comment
      const commentDocRef = doc(db, 'comments', commentToDelete.docId);
      
      // Get the current comments array
      const commentDoc = await getDoc(commentDocRef);
      if (!commentDoc.exists()) {
        console.error('Comment doc not found');
        toast.error('Comment not found');
        return;
      }
      
      const commentsArray = commentDoc.data().comments;
      console.log('Current comments array size:', commentsArray.length);
      
      // Remove the comment by index
      const updatedComments = commentsArray.filter((_, index) => index !== commentToDelete.commentIndex);
      console.log('Updated comments array size:', updatedComments.length);
      
      if (updatedComments.length === 0) {
        // If no comments left, update with empty array instead of deleting
        console.log('No comments left, updating with empty array');
        try {
          await updateDoc(commentDocRef, {
            comments: []
          });
          console.log('Empty array update succeeded');
        } catch (emptyUpdateError) {
          console.error('Empty array update failed:', emptyUpdateError);
          throw emptyUpdateError;
        }
      } else {
        // Update the document with the filtered comments array
        console.log('Updating comments array');
        try {
          await updateDoc(commentDocRef, {
            comments: updatedComments
          });
          console.log('Comments array update succeeded');
        } catch (updateError) {
          console.error('Comments array update failed:', updateError);
          throw updateError;
        }
      }
      
      // Also update the commentCount field in the post document
      console.log('Updating post comment count');
      try {
        const postRef = doc(db, 'posts', post.id);
        await updateDoc(postRef, {
          commentCount: Math.max((post.commentCount || 0) - 1, 0)
        });
        console.log('Post comment count updated successfully');
      } catch (postUpdateError) {
        console.error('Post comment count update failed:', postUpdateError);
        // Continue since the comment was deleted even if the count update fails
      }
      
      toast.success('Comment deleted successfully');
      
      // Notify the parent component that a comment has been deleted
      if (typeof onPostUpdated === 'function') {
        onPostUpdated();
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      console.log('Error code:', error.code);
      console.log('Error message:', error.message);
      
      if (error.code === 'permission-denied') {
        // Still show success if the operation is denied but we can see the comment is gone
        console.log('Permission denied but continuing with UI update');
        toast.success('Comment deleted');
        if (typeof onPostUpdated === 'function') {
          onPostUpdated();
        }
      } else {
        toast.error('Failed to delete comment');
      }
    } finally {
      setShowDeleteCommentModal(false);
      setCommentToDelete(null);
    }
  };

  const handleVote = async (voteType) => {
    if (!post || !post.id) return;
    
    try {
      const postRef = doc(db, 'posts', post.id);
      const postDoc = await getDoc(postRef);
      
      if (!postDoc.exists()) {
        toast.error('Post not found');
        return;
      }

      const postData = postDoc.data();
      const currentUpvotes = postData.upvoteCount || 0;
      const currentDownvotes = postData.downvoteCount || 0;
      const upvotedBy = postData.upvotedBy || [];
      const downvotedBy = postData.downvotedBy || [];

      let updates = {};

      // Handle upvote
      if (voteType === 'up') {
        if (upvotedBy.includes(currentUser.uid)) {
          // User is removing their upvote
          updates = {
            upvoteCount: currentUpvotes - 1,
            upvotedBy: arrayRemove(currentUser.uid)
          };
        } else {
          // User is adding an upvote
          updates = {
            upvoteCount: currentUpvotes + 1,
            upvotedBy: arrayUnion(currentUser.uid)
          };
          
          // If user had previously downvoted, remove it
          if (downvotedBy.includes(currentUser.uid)) {
            updates.downvoteCount = currentDownvotes - 1;
            updates.downvotedBy = arrayRemove(currentUser.uid);
          }
        }
      }
      // Handle downvote
      else if (voteType === 'down') {
        if (downvotedBy.includes(currentUser.uid)) {
          // User is removing their downvote
          updates = {
            downvoteCount: currentDownvotes - 1,
            downvotedBy: arrayRemove(currentUser.uid)
          };
        } else {
          // User is adding a downvote
          updates = {
            downvoteCount: currentDownvotes + 1,
            downvotedBy: arrayUnion(currentUser.uid)
          };
          
          // If user had previously upvoted, remove it
          if (upvotedBy.includes(currentUser.uid)) {
            updates.upvoteCount = currentUpvotes - 1;
            updates.upvotedBy = arrayRemove(currentUser.uid);
          }
        }
      }

      await updateDoc(postRef, updates);
      
      // Call the onPostUpdated callback to notify parent component about the vote
      if (typeof onPostUpdated === 'function') {
        onPostUpdated();
      }
      
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('Failed to vote on post');
    }
  };

  const handleFollowToggle = async () => {
    if (!post || !post.id || followLoading) return;
    
    setFollowLoading(true);
    try {
      const followingRef = doc(db, 'followingPosts', currentUser.uid);
      const followingDoc = await getDoc(followingRef);
      
      if (!followingDoc.exists()) {
        // Create new following document
        if (!isFollowing) {
          await setDoc(followingRef, {
            userId: currentUser.uid,
            posts: [post.id],
            createdAt: new Date(),
            updatedAt: new Date()
          });
          setIsFollowing(true);
          toast.success('Following this post');
        }
      } else {
        // Update existing document
        const followingData = followingDoc.data();
        const currentPosts = followingData.posts || [];
        
        if (isFollowing) {
          // Unfollow - remove post from array
          const updatedPosts = currentPosts.filter(postId => postId !== post.id);
          await updateDoc(followingRef, {
            posts: updatedPosts,
            updatedAt: new Date()
          });
          setIsFollowing(false);
          toast.success('Unfollowed this post');
        } else {
          // Follow - add post to array
          if (!currentPosts.includes(post.id)) {
            await updateDoc(followingRef, {
              posts: arrayUnion(post.id),
              updatedAt: new Date()
            });
            setIsFollowing(true);
            toast.success('Following this post');
          }
        }
      }
    } catch (error) {
      console.error('Error toggling follow status:', error);
      toast.error('Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = typeof timestamp.toDate === 'function' ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  if (!isOpen || !post) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto">
      <AnimatePresence>
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="p-3 sm:p-5 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900/30 dark:to-indigo-900/30">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white pr-2 break-words">{post?.postTitle || 'Post Details'}</h2>
            <button
              onClick={onClose}
              className="p-2 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full shadow-md transition-colors flex-shrink-0"
            >
              <FaTimes className="text-gray-600 dark:text-gray-300" />
            </button>
          </div>

          <div className="flex flex-col md:flex-row">
            {/* Voting Sidebar - Horizontal on mobile, Vertical on desktop */}
            <div className="md:w-16 flex flex-row md:flex-col items-center justify-center md:justify-start gap-6 p-2 md:p-4 bg-gray-50 dark:bg-gray-700/50">
              <button
                onClick={() => handleVote('up')}
                className={`p-2 md:p-3 rounded-lg hover:bg-gray-200 dark:hover:bg-green-600/30 transition-colors ${
                  post?.upvotedBy?.includes(currentUser.uid) ? 'bg-green-100 dark:bg-green-700/30 text-green-600 dark:text-green-400' : 'text-gray-500 bg-white dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                <FaThumbsUp size={18} />
              </button>
              <button
                onClick={() => handleVote('down')}
                className={`p-2 md:p-3 rounded-lg hover:bg-gray-200 dark:hover:bg-red-600/30 transition-colors ${
                  post?.downvotedBy?.includes(currentUser.uid) ? 'bg-red-100 dark:bg-red-700/30 text-red-600 dark:text-red-400' : 'text-gray-500 bg-white dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                <FaThumbsDown size={18} />
              </button>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 150px)' }}>
              {/* Post Content */}
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                {/* Follow/Unfollow Button */}
                <div className="mb-4">
                  <button
                    onClick={handleFollowToggle}
                    disabled={followLoading}
                    className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                      isFollowing
                        ? 'bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white'
                        : 'bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white'
                    } ${followLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {followLoading ? (
                      <FiLoader className="animate-spin" />
                    ) : isFollowing ? (
                      <FaTimes />
                    ) : (
                      <FaHeart />
                    )}
                    <span>
                      {followLoading 
                        ? 'Updating...' 
                        : isFollowing 
                          ? 'Unfollow this post' 
                          : 'Follow this post'
                      }
                    </span>
                  </button>
                </div>

                {/* Post labels */}
                {post?.postLabels && post.postLabels.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="text-gray-600 dark:text-gray-400 flex items-center">
                      <FaTags className="mr-2" />
                    </span>
                    {post.postLabels.map(labelId => (
                      <span 
                        key={labelId}
                        className="px-2 pt-0.5 pb-1 text-xs rounded-full border bg-purple-100 text-purple-700 border-purple-200"
                      >
                        {labelsData[labelId] || labelId}
                      </span>
                    ))}
                  </div>
                )}

                {/* Post content */}
                <div className="bg-gray-50 dark:bg-gray-700/50 p-3 sm:p-5 rounded-lg my-4 border border-gray-200 dark:border-gray-600 shadow-sm">
                  <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap text-sm sm:text-base leading-relaxed">
                    {post?.postContent || 'No content available'}
                  </p>
                </div>
                
                {/* Post metadata */}
                <div className="mt-4 flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center">
                      {post?.isAdmin ? (
                        <FaUserCircle className="mr-1 sm:mr-2 text-indigo-500" size={16} />
                      ) : (
                        <FaUserCircle className="mr-1 sm:mr-2" size={16} />
                      )}
                      <span>{post.username}</span>
                      {post?.isAdmin && (
                        <motion.span 
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.3, type: "spring", stiffness: 200 }}
                          className="ml-1.5 px-2 pt-0.5 pb-1 bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900/30 dark:to-indigo-900/30 text-purple-700 dark:text-indigo-300 rounded text-xs font-medium border border-purple-200 dark:border-indigo-800/30 shadow-sm"
                        >
                          Admin
                        </motion.span>
                      )}
                  </div>
                  <div className="flex items-center">
                    <span>{formatDate(post?.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="flex items-center">
                      <FaThumbsUp className="text-green-500 mr-1" />
                      <span>{post?.upvoteCount || 0}</span>
                    </div>
                    <div className="flex items-center">
                      <FaThumbsDown className="text-red-500 mr-1" />
                      <span>{post?.downvoteCount || 0}</span>
                    </div>
                    <div className="flex items-center">
                      <FaRegComment className="mr-1" />
                      <span>{comments.length}</span>
                    </div>
                  </div>
                </div>

                {post?.lastEditedAt && post?.lastEditedAt !== post?.createdAt && (
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                    Last edited: {formatDate(post.lastEditedAt)}
                  </div>
                )}
              </div>

              {/* Comments Section */}
              <div className="p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6 flex items-center">
                  <FaRegComment className="mr-2" />
                  Comments ({comments.length})
                </h3>
                
                {/* Comment Form */}
                <form onSubmit={handleSubmitComment} className="mb-6 sm:mb-8">
                  <textarea
                    value={newComment}
                    onChange={(e) => {
                      if (e.target.value.length <= 250) {
                        setNewComment(e.target.value);
                      }
                    }}
                    placeholder="Write a comment... (max 250 characters)"
                    className="w-full p-3 sm:p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    rows="3"
                    maxLength={250}
                    disabled={isSubmittingComment}
                  />
                  <div className="flex justify-between items-center mt-2">
                    <div className={`text-xs ${newComment.length > 250 ? 'text-red-500' : newComment.length > 200 ? 'text-yellow-500' : 'text-gray-500'}`}>
                      {newComment.length}/250 characters {newComment.length > 250 ? '(limit exceeded)' : ''}
                    </div>
                    <button
                      type="submit"
                      disabled={!newComment.trim() || newComment.length > 250 || !post?.id || isSubmittingComment}
                      className={`px-4 sm:px-6 py-2 rounded-lg text-white font-medium flex items-center gap-2 shadow-md transition-all ${
                        newComment.trim() && newComment.length <= 250 && post?.id && !isSubmittingComment
                          ? 'bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700'
                          : 'bg-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {isSubmittingComment ? (
                        <>
                          <FiLoader className="animate-spin" />
                          <span>Posting...</span>
                        </>
                      ) : (
                        <>
                          <FaRegComment />
                          <span>Post</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {/* Comments List */}
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-500 border-opacity-30 border-t-purple-500 mx-auto"></div>
                  </div>
                ) : comments.length === 0 ? (
                  <div className="text-center py-10 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                    <FaRegComment className="mx-auto mb-3 text-gray-400 dark:text-gray-500" size={30} />
                    <p>No comments yet. Be the first to comment!</p>
                  </div>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {comments.map((comment, index) => (
                      <motion.div 
                        key={`${comment.docId}-${comment.commentIndex}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white dark:bg-gray-700 rounded-lg p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center">
                            {comment.isAdmin ? (
                              <FaUserCircle className="text-indigo-500 dark:text-indigo-400 mr-1 sm:mr-2" size={16} />
                            ) : (
                              <FaUserCircle className="text-gray-500 dark:text-gray-400 mr-1 sm:mr-2" size={16} />
                            )}
                            <div>
                              <span className="font-medium text-gray-900 dark:text-white text-sm">{comment.username}</span>
                              {comment.isAdmin && (
                                <motion.span 
                                  initial={{ scale: 0.8, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  transition={{ duration: 0.3, type: "spring", stiffness: 200 }}
                                  className="ml-1.5 px-2 pt-0.5 pb-1 bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900/30 dark:to-indigo-900/30 text-purple-700 dark:text-indigo-300 rounded text-xs font-medium border border-purple-200 dark:border-indigo-800/30 shadow-sm"
                                >
                                  Admin
                                </motion.span>
                              )}
                              <span className="text-xs text-gray-500 ml-2">{formatDate(comment.createdAt)}</span>
                            </div>
                          </div>
                          {(currentUser.uid === comment.userId || isAdmin) && (
                            <button
                              onClick={() => confirmDeleteComment(comment)}
                              className="text-red-500 hover:text-red-600 p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            >
                              <FaTrash size={14} />
                            </button>
                          )}
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 text-sm sm:text-base">{comment.content}</p>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Confirmation Modal for Comment Deletion */}
      <ConfirmationModal
        isOpen={showDeleteCommentModal}
        onClose={() => {
          setShowDeleteCommentModal(false);
          setCommentToDelete(null);
        }}
        onConfirm={handleDeleteComment}
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
      />
    </div>
  );
}

export default ShowCompletePostModal;